import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertOctagon, AlertTriangle, Info, ShieldCheck,
  OctagonX, AlertCircle, BarChart3, Layers, ExternalLink,
  Zap, UserX, Quote, Users, ShieldQuestion,
  Cpu, ShieldAlert, XCircle, CheckCircle2, HelpCircle,
  TrendingUp, Timer, Plus, ChevronDown, Tag, BarChart2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type {
  VerdictResult, VerdictSummary, KeyFinding,
} from '../../../types/analysis-session';
import type { TechniqueDefinition } from '../../../services/technique-definitions.service';
import { localizedExplanation } from '../../../utils/i18n-fields';
import { enumLabel } from '../../../utils/i18n-enums';
import type { ComponentStatus } from '../types';
import { RISK_COLORS, getViralityColor, safeHostname } from '../utils';
import {
  VerdictSection,
  ActionCallout, ActionIconBox, ActionBody, ActionEyebrow, ActionHeadline, ActionText,
  CollapseBtn, CollapseBtnLeft, CollapseBtnChevron,
  StatBlock, ScoresBlock, ScoreLine, ScoreLineName, ScoreLineBar, ScoreLineFill, ScoreLineNum, ScoreLineSkipped,
  ChipsRow, Chip,
  LegacyExplanation,
} from '../styles';
import { HeadlineCard } from './HeadlineCard';
import { FindingCard, type FindingAccent } from './FindingCard';
import { SectionDivider } from './SectionDivider';
import { renderTechniquesEditorial } from '../details/techniques';
import { renderAiEditorial } from '../details/ai';
import { renderClaimsEditorial } from '../details/claims';
import { renderSourceEditorial } from '../details/source';

interface Props {
  verdict: VerdictResult;
  totalDuration: number | null;
  components: Record<string, ComponentStatus>;
  fullResult: Record<string, any> | null;
  techniqueDefinitions: TechniqueDefinition[];
}

const SEVERITY_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };

/** Backend occasionally leaks debug strings (e.g. `ai_probability=85/100, verdict="LIKELY_AI"`,
 * `Claims: [SKIPPED]`, `data_quality=full`) into evidence_ref.quote. Hide those — keep real quotes. */
function cleanQuote(q: string | null | undefined): string | undefined {
  if (!q) return undefined;
  if (/\[SKIPPED\]/i.test(q)) return undefined;
  if (/^\s*\w+\s*=\s*[\d"']/.test(q)) return undefined;
  if (/data_quality\s*=/i.test(q)) return undefined;
  return q;
}

/** Map finding severity → icon + accent. */
function findingIcon(f: KeyFinding): { icon: LucideIcon; accent: FindingAccent } {
  const sev = f.severity;
  const type = f.type;
  // Type-based mapping (richer than just severity)
  const TYPE_ICONS: Record<string, LucideIcon> = {
    false_claim: BarChart3,
    fabricated_quote: Quote,
    manipulation: AlertCircle,
    urgency: Zap,
    imposter_content: UserX,
    conspiracy: ShieldQuestion,
    demographic: Users,
  };
  const accent: FindingAccent = sev === 'critical' ? 'critical' : sev === 'warning' ? 'warning' : 'info';
  return { icon: TYPE_ICONS[type as string] || AlertCircle, accent };
}

/** Action callout eyebrow translation key by severity. */
function actionEyebrowKey(severity: string): string {
  if (severity === 'critical') return 'analysis.verdict.actions.doNotShare';
  if (severity === 'warning')  return 'analysis.verdict.actions.readCritically';
  return 'analysis.verdict.actions.note';
}

/** Category icon by risk_category_color. */
function categoryIcon(color: string): LucideIcon {
  if (color === 'red' || color === 'darkred') return AlertOctagon;
  if (color === 'orange') return AlertTriangle;
  if (color === 'yellow') return Info;
  if (color === 'green' || color === 'lightgreen') return ShieldCheck;
  return Info;
}

/** Animated count-up — re-runs when target changes. */
function useCountUp(target: number, duration = 900, delay = 200): number {
  const [val, setVal] = useState(0);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    let start: number | null = null;
    const begin = (ts: number) => { start = ts; tick(ts); };
    const tick = (ts: number) => {
      if (start == null) start = ts;
      const t = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(target * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    const timer = setTimeout(() => { rafRef.current = requestAnimationFrame(begin); }, delay);
    return () => {
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, delay]);
  return val;
}

export const Verdict: React.FC<Props> = ({
  verdict, totalDuration, components, fullResult, techniqueDefinitions,
}) => {
  const { t, i18n } = useTranslation();
  const lang: 'ro' | 'en' = (i18n.language || '').toLowerCase().startsWith('ro') ? 'ro' : 'en';
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [techExpand, setTechExpand] = useState(false);
  const [claimsExpand, setClaimsExpand] = useState(false);

  const summary = (verdict.context_summary as { verdict_summary?: VerdictSummary } | undefined)?.verdict_summary;
  const accent = RISK_COLORS[verdict.risk_category_color] || '#94a3b8';

  const animatedScore = useCountUp(verdict.risk_score, 900, 1100);
  void animatedScore; // gauge fills via CSS keyframe; we use it visually for text

  const sortedFindings = useMemo<KeyFinding[]>(() => {
    if (!summary?.key_findings) return [];
    return [...summary.key_findings].sort(
      (a, b) => (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3),
    );
  }, [summary]);

  const dominant = sortedFindings[0];
  const rest = sortedFindings.slice(1);

  const CatIcon = categoryIcon(verdict.risk_category_color);

  // Tier 4 chips data
  const techniquesCount = verdict.context_summary?.techniques_detected ?? 0;
  const falseClaims = verdict.context_summary?.claims_false ?? 0;
  const elapsedSec = totalDuration ? `${(totalDuration / 1000).toFixed(1)}s` : null;
  const viralityScore = verdict.virality_score;
  const viralityLevel = verdict.virality_level;

  const scoreEntries: { name: string; value: number | null }[] = [
    { name: t('analysis.verdict.scores.manipulation'), value: verdict.score_manipulation ?? null },
    { name: 'Claims', value: verdict.score_claims ?? null },
    { name: t('analysis.verdict.scores.aiGeneration'), value: verdict.score_ai ?? null },
    { name: t('analysis.verdict.scores.source'), value: verdict.score_source ?? null },
  ];

  const hasComponentData = !!(fullResult?.techniques || fullResult?.ai_tampered ||
                              fullResult?.claims || fullResult?.source_assessment);

  return (
    <VerdictSection>
      {summary ? (
        <>
          {/* ─── TIER 1 — HEADLINE cu gauge ─── */}
          <HeadlineCard
            accent={accent}
            score={verdict.risk_score}
            scoreLabel={`/ 100 ${t('analysis.verdict.riskBadge')}`}
            eyebrow={t('analysis.verdict.riskAnalyzed')}
            icon={CatIcon}
            category={enumLabel(verdict.risk_category || '').replace(/_/g, ' ')}
            descriptor={
              <>
                {verdict.risk_level && (
                  <>
                    <span>{enumLabel(verdict.risk_level)}</span>
                    <span className="sep">·</span>
                  </>
                )}
                {verdict.confidence != null && (
                  <span>{t('analysis.verdict.confidence')} {verdict.confidence}%</span>
                )}
              </>
            }
            tldr={(summary[`tl_dr_${lang}` as 'tl_dr_en' | 'tl_dr_ro'] as string | undefined) || summary.tl_dr_en || summary.tl_dr_ro || ''}
          />

          {/* ─── TIER 2 — ACTION CALLOUT ─── */}
          {(summary.what_to_do_ro || summary.what_to_do_en) && (
            <ActionCallout accent={accent}>
              <ActionIconBox accent={accent}>
                <OctagonX size={26} strokeWidth={2} />
              </ActionIconBox>
              <ActionBody>
                <ActionEyebrow accent={accent}>
                  {t(actionEyebrowKey(dominant?.severity || 'info'))}
                </ActionEyebrow>
                <ActionHeadline>
                  {dominant?.severity === 'critical'
                    ? t('analysis.verdict.actions.doNotShareHeadline')
                    : t('analysis.verdict.actions.readCarefully')}
                </ActionHeadline>
                <ActionText>{(summary[`what_to_do_${lang}` as 'what_to_do_en' | 'what_to_do_ro'] as string | undefined) || summary.what_to_do_en || summary.what_to_do_ro || ''}</ActionText>
              </ActionBody>
            </ActionCallout>
          )}

          {/* ─── TIER 3 — DOMINANT FINDING ─── */}
          {dominant && (
            <>
              <SectionDivider label={t('analysis.verdict.topIssue')} />
              {(() => {
                const { icon, accent: a } = findingIcon(dominant);
                return (
                  <FindingCard
                    dominant
                    icon={icon}
                    accent={a}
                    headline={dominant[lang] || dominant.en || dominant.ro || ''}
                    quote={cleanQuote(dominant.evidence_ref?.quote)}
                    source={dominant.evidence_ref?.source_url ? (
                      <>
                        <ExternalLink size={13} strokeWidth={2} />
                        <span>{t('analysis.verdict.verifiedBy')}</span>
                        <a
                          href={dominant.evidence_ref.source_url}
                          target="_blank" rel="noopener noreferrer"
                          style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 500 }}
                        >
                          {safeHostname(dominant.evidence_ref.source_url)}
                        </a>
                      </>
                    ) : undefined}
                  />
                );
              })()}

              {/* secondary findings collapse */}
              {rest.length > 0 && (
                <>
                  <CollapseBtn onClick={() => setSecondaryOpen(o => !o)}>
                    <CollapseBtnLeft>
                      <Layers size={16} strokeWidth={2} />
                      <span>
                        {secondaryOpen
                          ? t('analysis.verdict.hideOtherSignals')
                          : t('analysis.verdict.showMoreSignals', { count: rest.length })}
                      </span>
                    </CollapseBtnLeft>
                    <CollapseBtnChevron expanded={secondaryOpen}>
                      <ChevronDown size={14} strokeWidth={2.5} />
                    </CollapseBtnChevron>
                  </CollapseBtn>

                  {secondaryOpen && (
                    <>
                      <SectionDivider
                        label={t('analysis.verdict.otherSignalsLabel')}
                        count={rest.length}
                      />
                      {rest.map((f, idx) => {
                        const { icon, accent: a } = findingIcon(f);
                        return (
                          <FindingCard
                            key={idx}
                            icon={icon}
                            accent={a}
                            headline={f[lang] || f.en || f.ro || ''}
                            quote={cleanQuote(f.evidence_ref?.quote)}
                            source={f.evidence_ref?.source_url ? (
                              <>
                                <ExternalLink size={13} strokeWidth={2} />
                                <a
                                  href={f.evidence_ref.source_url}
                                  target="_blank" rel="noopener noreferrer"
                                  style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 500 }}
                                >
                                  {safeHostname(f.evidence_ref.source_url)}
                                </a>
                              </>
                            ) : (
                              <>
                                <Tag size={13} strokeWidth={2} />
                                <span>{f.type.replace(/_/g, ' ')}</span>
                              </>
                            )}
                          />
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* ─── TIER 4 — DETALII TEHNICE (collapse) ─── */}
          <CollapseBtn onClick={() => setDetailsOpen(o => !o)}>
            <CollapseBtnLeft>
              <BarChart3 size={16} strokeWidth={2} />
              <span>{t('analysis.verdict.technicalDetails')}</span>
            </CollapseBtnLeft>
            <CollapseBtnChevron expanded={detailsOpen}>
              <ChevronDown size={14} strokeWidth={2.5} />
            </CollapseBtnChevron>
          </CollapseBtn>

          {detailsOpen && (
            <>
              {/* Stat block — score bars + chips */}
              <StatBlock>
                <ScoresBlock>
                  {scoreEntries.map(({ name, value }, i) => (
                    <ScoreLine key={name}>
                      <ScoreLineName>{name}</ScoreLineName>
                      <ScoreLineBar>
                        {value != null && (
                          <ScoreLineFill
                            width={value}
                            color={value >= 70 ? '#ef4444' : value >= 40 ? '#f97316' : '#22c55e'}
                            delay={200 + i * 80}
                          />
                        )}
                      </ScoreLineBar>
                      {value != null
                        ? <ScoreLineNum>{value.toFixed(0)}</ScoreLineNum>
                        : <ScoreLineSkipped>—</ScoreLineSkipped>}
                    </ScoreLine>
                  ))}
                </ScoresBlock>
                <ChipsRow>
                  {viralityScore != null && (
                    <Chip variant={viralityScore >= 50 ? 'critical' : viralityScore >= 25 ? 'warning' : 'success'}>
                      <TrendingUp size={12} strokeWidth={2} />
                      Virality {viralityScore}{viralityLevel ? ` ${enumLabel(viralityLevel)}` : ''}
                    </Chip>
                  )}
                  {techniquesCount > 0 && (
                    <Chip variant="violet">
                      <Layers size={12} strokeWidth={2} />
                      {techniquesCount} {t('analysis.verdict.chips.techniquesDetected')}
                    </Chip>
                  )}
                  {falseClaims > 0 && (
                    <Chip variant="critical">
                      <XCircle size={12} strokeWidth={2} />
                      {falseClaims} {t('analysis.verdict.chips.falseClaims')}
                    </Chip>
                  )}
                  {elapsedSec && (
                    <Chip variant="neutral">
                      <Timer size={12} strokeWidth={2} />
                      {elapsedSec} {t('analysis.verdict.chips.analysisLabel')}
                    </Chip>
                  )}
                </ChipsRow>
              </StatBlock>

              {/* AI Detection */}
              {fullResult?.ai_tampered && renderAiEditorial(fullResult.ai_tampered, t)}

              {/* Source Assessment */}
              {fullResult?.source_assessment && renderSourceEditorial(fullResult.source_assessment, t)}

              {/* Techniques */}
              {fullResult?.techniques && renderTechniquesEditorial(fullResult.techniques, techniqueDefinitions, t, techExpand, () => setTechExpand(o => !o))}

              {/* Claims */}
              {fullResult?.claims && renderClaimsEditorial(fullResult.claims, t, claimsExpand, () => setClaimsExpand(o => !o))}

              {/* If we have components but no detail data */}
              {!hasComponentData && (
                <FindingCard
                  icon={Info}
                  accent="neutral"
                  headline={t('analysis.verdict.noComponentData')}
                />
              )}
            </>
          )}
        </>
      ) : (
        // ═══ LEGACY FALLBACK ═══
        <>
          <HeadlineCard
            accent={accent}
            score={verdict.risk_score}
            scoreLabel={`/ 100 ${t('analysis.verdict.riskBadge')}`}
            eyebrow={t('analysis.verdict.riskAnalyzed')}
            icon={CatIcon}
            category={enumLabel(verdict.risk_category || '').replace(/_/g, ' ')}
            descriptor={verdict.confidence != null
              ? <span>{t('analysis.verdict.confidence')} {verdict.confidence}%</span>
              : undefined}
          />

          {(verdict.explanation_en || verdict.explanation_ro) && (
            <LegacyExplanation>{localizedExplanation(verdict)}</LegacyExplanation>
          )}

          <CollapseBtn onClick={() => setDetailsOpen(o => !o)}>
            <CollapseBtnLeft>
              <BarChart3 size={16} strokeWidth={2} />
              <span>{t('analysis.verdict.technicalDetails')}</span>
            </CollapseBtnLeft>
            <CollapseBtnChevron expanded={detailsOpen}>
              <ChevronDown size={14} strokeWidth={2.5} />
            </CollapseBtnChevron>
          </CollapseBtn>

          {detailsOpen && (
            <>
              <StatBlock>
                <ScoresBlock>
                  {scoreEntries.map(({ name, value }, i) => (
                    <ScoreLine key={name}>
                      <ScoreLineName>{name}</ScoreLineName>
                      <ScoreLineBar>
                        {value != null && (
                          <ScoreLineFill
                            width={value}
                            color={value >= 70 ? '#ef4444' : value >= 40 ? '#f97316' : '#22c55e'}
                            delay={200 + i * 80}
                          />
                        )}
                      </ScoreLineBar>
                      {value != null
                        ? <ScoreLineNum>{value.toFixed(0)}</ScoreLineNum>
                        : <ScoreLineSkipped>—</ScoreLineSkipped>}
                    </ScoreLine>
                  ))}
                </ScoresBlock>
                <ChipsRow>
                  {viralityScore != null && (
                    <Chip variant={viralityScore >= 50 ? 'critical' : 'warning'}>
                      <TrendingUp size={12} strokeWidth={2} />
                      Virality {viralityScore}
                    </Chip>
                  )}
                  {elapsedSec && (
                    <Chip variant="neutral">
                      <Timer size={12} strokeWidth={2} />
                      {elapsedSec}
                    </Chip>
                  )}
                </ChipsRow>
              </StatBlock>
            </>
          )}
        </>
      )}
    </VerdictSection>
  );
};
